import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";

import { GetVariables } from "../../../services/api/Listagens/GetVariables";
import { ExcelInsert } from "../../../services/api/Listagens/ExcelInsert";
import { notify } from "../../../shared/utils/utils";

import {
  buildVariablesByCategory,
  flattenVariables,
  normalize,
} from "../utils/sheetsAdd.utils";

export default function useExcelAddController({
  setAlreadyFetchSheets,
}) {
  const [padronizationPage, setPadronizationPage] = useState(0);
  const [dataR, setDataR] = useState([]);
  const [variablesOfTemplate, setVariablesOfTemplate] = useState({});

  const allVariables = useMemo(
    () => flattenVariables(variablesOfTemplate),
    [variablesOfTemplate]
  );

  /* =========================
     Carrega variáveis do sistema
  ========================= */
  useEffect(() => {
    async function fetchVariables() {
      try {
        const resultJson = await GetVariables();
        const grouped = buildVariablesByCategory(resultJson?.data || []);
        setVariablesOfTemplate(grouped);
      } catch (err) {
        console.error(err);
      }
    }

    fetchVariables();
  }, []);

  /* =========================
     Step 1: Lê arquivo Excel
  ========================= */
  async function handleCheckExcelPermission(formData) {
    if (!formData?.file) {
      notify("Error", "Selecione um arquivo Excel.", true);
      return false;
    }

    try {
      const file = formData.file;
      const data = await file.arrayBuffer();

      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (!jsonData.length) {
        notify("Error", "O arquivo não possui dados válidos.", true);
        return false;
      }

      const firstRow = jsonData[0];

      const items = Object.entries(firstRow).map(
        ([field, value], index) => {
          const match = allVariables.find(
            (v) => normalize(v) === normalize(field)
          );

          return {
            field,
            value,
            column_index: index,
            variable: match || null,
          };
        }
      );

      setDataR(items);
      setPadronizationPage(0);

      return true;
    } catch (err) {
      console.error(err);
      notify("Error", "Erro ao processar o arquivo Excel.", true);
      return false;
    }
  }

  /* =========================
     Validação
  ========================= */
  function validateBeforeFinish() {
    const emptyCount = dataR.filter((x) => !x.variable).length;

    const hasChannel =
      dataR.some((x) => x.variable === "WhatsApp") ||
      dataR.some((x) => x.variable === "Email");

    if (!hasChannel) {
      notify(
        "Alert",
        "É obrigatório selecionar a coluna de WhatsApp ou Email.",
        true
      );
      return { valid: false };
    }

    return { valid: true, emptyCount };
  }

  /* =========================
     Submit final (EXCEL REAL)
  ========================= */
  async function handleSubmit({ formData, onSuccess }) {
    try {
      if (!formData?.file) {
        notify("Error", "Arquivo não encontrado.", true);
        return;
      }

      const multipart = new FormData();

      // 📎 Arquivo binário
      multipart.append("file", formData.file);

      // 🔗 Variáveis mapeadas
      const variables = dataR
        .filter((item) => item?.field && item?.variable)
        .map((item) => ({
          field: item.field,
          variable: item.variable,
        }));

      multipart.append("variables", JSON.stringify(variables));

      // 📝 Dados básicos
      multipart.append("title", formData?.title || "");
      multipart.append("desc", formData?.desc || "");

      // 👀 item_example (primeira linha do Excel)
      const exampleRow = {};
      dataR.forEach((item) => {
        if (item.field) {
          exampleRow[item.field] = item.value;
        }
      });

      multipart.append("item_example", JSON.stringify(exampleRow));

      const response = await ExcelInsert(multipart);

      if (response?.status === "success") {
        setAlreadyFetchSheets(false);
        notify("Success", "Listagem adicionada com sucesso!", true);
        onSuccess?.();
      } else {
        notify("Error", "Erro ao enviar listagem.", true);
      }
    } catch (err) {
      console.error(err);
      notify("Error", "Erro ao enviar os dados", true);
    }
  }

  return {
    padronizationPage,
    dataR,
    variablesOfTemplate,
    setPadronizationPage,
    setDataR,
    handleCheckExcelPermission,
    validateBeforeFinish,
    handleSubmit,
  };
}